import React from "react";

import {
  FileText,
  Link as LinkIcon,
  ListChecks,
  type LucideIcon,
  ShieldAlert,
  Sparkles,
  TrendingUp,
  TriangleAlert,
} from "lucide-react";

import { ACCENT, Modal } from "./Chrome";
import SectionBody from "./SectionBody";
import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import type { XFactorSectionDetail } from "../../../Types/XFactorViewsRendering";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";
import {
  BAND_FILL,
  bandOf,
  formatDate,
  getSectionDetail,
  presentSectionIds,
} from "../lib/derive";

/**
 * The left anchor rail. The set and order are xfactor's own, read off its
 * button aria-labels; each is disabled when the section carries nothing for it.
 */
const ANCHORS: Array<{
  id: string;
  label: string;
  Icon: LucideIcon;
  has: (d: XFactorSectionDetail) => boolean;
}> = [
  {
    id: "synopsis",
    label: "Synopsis",
    Icon: FileText,
    has: d => Boolean(d.synopsis),
  },
  {
    id: "highlights",
    label: "Highlights",
    Icon: Sparkles,
    has: d => d.highlights.length > 0,
  },
  {
    id: "lowlights",
    label: "Lowlights",
    Icon: TriangleAlert,
    has: d => d.lowlights.length > 0,
  },
  {
    id: "opportunities",
    label: "Opportunities",
    Icon: TrendingUp,
    has: d => d.opportunities.length > 0,
  },
  {
    id: "risks",
    label: "Risks",
    Icon: ShieldAlert,
    has: d => d.risks.length > 0,
  },
  {
    id: "actions",
    label: "Recommended Actions",
    Icon: ListChecks,
    has: d => d.actions.length > 0,
  },
  {
    id: "citations",
    label: "Citations",
    Icon: LinkIcon,
    has: d => d.citations.length > 0,
  },
];

/** A text button in the modal's bottom navigation bar. */
const NavButton: React.FC<{
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ disabled, onClick, title, children }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    title={title}
    className="whitespace-nowrap font-medium transition-colors disabled:cursor-default disabled:text-gray-300 enabled:text-[#7C3AED] enabled:hover:underline"
  >
    {children}
  </button>
);

/**
 * One section's detail, with left/right navigation across sections.
 *
 * xfactor reaches this from three places — a rail dot, a matrix bubble, and the
 * Full Briefing — and renders the same body in all three. Same here: this is
 * the only component that renders a section body.
 */
const SectionDetailModal: React.FC<{
  open: boolean;
  onClose: () => void;
  accountName: string;
  digestDate?: string | null;
  data?: XFactorDigestData | null;
  sectionId: string;
  onNavigate: (sectionId: string) => void;
  /**
   * Account stepping, matching EB's own section modal. Optional: the Account
   * Synopsis opens this for one account and has no list to step through, so it
   * gets the section half of the bar and no account controls.
   */
  accountIndex?: number;
  accountTotal?: number;
  onStepAccount?: (delta: 1 | -1) => void;
}> = ({
  open,
  onClose,
  accountName,
  digestDate,
  data,
  sectionId,
  onNavigate,
  accountIndex,
  accountTotal,
  onStepAccount,
}) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const order = React.useMemo(
    () => ["summary", ...presentSectionIds(data)],
    [data]
  );
  const detail = React.useMemo(
    () => getSectionDetail(sectionId, data),
    [sectionId, data]
  );

  // The hero circle carries the section's own composite, falling back to
  // health when the composite is absent.
  const heroScore =
    detail.scores.find(x => x.label === "Health")?.value ?? null;

  const jump = (id: string) => {
    document
      .getElementById(`xf-sec-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const index = order.indexOf(sectionId);
  const prev = index > 0 ? order[index - 1] : null;
  const next = index >= 0 && index < order.length - 1 ? order[index + 1] : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      /* Same width as the Exec Profile Cards modal (`Modal`'s default), because
         it renders the same profile cards. At max-w-4xl the DISC tiles were
         narrow enough to wrap "Conscientiousness" onto a second line, which
         made that tile taller than its three neighbours. One width for every
         section, since this component renders all of them. */
      width="max-w-6xl"
      title={
        <span className="flex items-center gap-2.5">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{
              backgroundColor: BAND_FILL[bandOf(heroScore, bhThresholds)],
            }}
          >
            {heroScore == null ? "—" : Math.round(heroScore)}
          </span>
          <span>
            {detail.label}:{" "}
            <span style={{ color: "#ad46ff" }}>{accountName}</span>
          </span>
        </span>
      }
      subtitle={formatDate(digestDate) || digestDate}
      /* Navigation lives in the bottom bar only, as it does on EB's own section
         modal — the header chevrons that used to sit here were a second place
         to do the same thing. */
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-3">
            {onStepAccount ? (
              <>
                <NavButton
                  disabled={(accountIndex ?? 0) <= 0}
                  onClick={() => onStepAccount(-1)}
                  title="Previous account"
                >
                  ↑ Prev Account
                </NavButton>
                <NavButton
                  disabled={
                    accountTotal == null ||
                    (accountIndex ?? 0) >= accountTotal - 1
                  }
                  onClick={() => onStepAccount(1)}
                  title="Next account"
                >
                  ↓ Next Account
                </NavButton>
              </>
            ) : null}
          </div>

          <div className="text-gray-500">
            {onStepAccount && accountTotal != null
              ? `Account ${(accountIndex ?? 0) + 1} of ${accountTotal} · `
              : ""}
            Section: {detail.label}
          </div>

          <div className="flex items-center gap-3">
            <NavButton
              disabled={!prev}
              onClick={() => prev && onNavigate(prev)}
              title="Previous section"
            >
              ← Prev Section
            </NavButton>
            <NavButton
              disabled={!next}
              onClick={() => next && onNavigate(next)}
              title="Next section"
            >
              → Next Section
            </NavButton>
          </div>
        </div>
      }
    >
      <div className="flex gap-4">
        {/* Left anchor rail. xfactor's own aria-labels give the exact set:
            Synopsis, Highlights, Lowlights, Opportunities, Risks, Recommended
            Actions, Citations. */}
        {/* `self-start` is what makes the sticky work: as a plain flex child the
            nav is stretched to the full height of the row, leaving it no room to
            travel, so it scrolled away with the content. Sized to its own
            content it can pin to the top of the modal's scroll area. */}
        <nav className="sticky top-0 flex shrink-0 flex-col items-center gap-1 self-start pt-1">
          {ANCHORS.map(a => {
            const enabled = !detail.missing && a.has(detail);
            return (
              <button
                key={a.id}
                type="button"
                disabled={!enabled}
                onClick={() => jump(a.id)}
                title={a.label}
                aria-label={a.label}
                className="rounded p-1.5 transition-colors enabled:hover:bg-purple-50 disabled:opacity-25"
                style={{ color: enabled ? ACCENT : "#cbd5e1" }}
              >
                <a.Icon size={14} />
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1">
          <SectionBody detail={detail} anchored data={data} />
        </div>
      </div>
    </Modal>
  );
};

export default SectionDetailModal;
